import apiClient from "@/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { logger } from "@/lib/default-logger";

export type RateCardSection = "setup" | "services" | "whatsapp" | "usage" | "additional";

export interface RateCardItem {
	id: string;
	code: string;
	section: RateCardSection;
	position: number;
	name: string;
	description: string | null;
	/** Decimal string, excluding GST, or null where `priceNote` stands in for a price. */
	amount: string | null;
	currency: string;
	unit: string | null;
	priceNote: string | null;
	/** Clients can tick it on the request form as an extra service. */
	optional: boolean;
	updatedAt: string;
	updatedByName: string | null;
}

export interface UpdateRateCardItemPayload {
	name: string;
	description: string | null;
	amount: number | null;
	unit: string | null;
	priceNote: string | null;
	optional: boolean;
}

export const rateCardKeys = {
	all: ["rateCard"] as const,
};

function errorMessage(error: any, fallback: string): string {
	const message = error?.response?.data?.message ?? error?.response?.data?.errors;
	if (Array.isArray(message)) return message.join(" ");
	return message || fallback;
}

/** The one rate card every client sees, in the order it reads. */
export function useRateCard() {
	return useQuery({
		queryKey: rateCardKeys.all,
		queryFn: async (): Promise<RateCardItem[]> => {
			const response = await apiClient.get("/rate-card");
			return response.data;
		},
	});
}

export function useUpdateRateCardItem() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, data }: { id: string; data: UpdateRateCardItemPayload }): Promise<RateCardItem> => {
			const response = await apiClient.put(`/admin/rate-card/${id}`, data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: rateCardKeys.all });
			toast.success("Rate card updated");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "Could not update the rate card"));
		},
	});
}
